import axios, { AxiosPromise, AxiosRequestConfig } from 'axios';
import { API_URL } from 'src/config/env';
import UserAuthService from '../user-auth';
import Router from '../../navigation/router';
import routes from '../../navigation/routes';

// Set default params and headers for axios
axios.defaults.baseURL = API_URL;
axios.defaults.headers.common['Content-Type'] = 'application/json;charset=utf-8';
axios.defaults.headers.common.Accept = 'application/json';

let isRefreshing = false;
let failedQueue: Array<{
    resolve: (token: string) => void;
    reject: (error: any) => void;
}> = [];

const processQueue = (error: any, token: string | null = null) => {
    failedQueue.forEach((prom) => {
        if (error) {
            prom.reject(error);
        } else if (token) {
            prom.resolve(token);
        } else {
            prom.reject(new Error('Token refresh failed'));
        }
    });
    failedQueue = [];
};

// Global response interceptor: silently refresh expired token or gracefully redirect to login
axios.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error?.config;
        if (error?.response?.status === 401 && originalRequest && !originalRequest._retry) {
            // Avoid looping if the refresh endpoint itself returns 401
            if (originalRequest.url && originalRequest.url.includes('/auth/refresh')) {
                await UserAuthService.logout();
                if (window.location.pathname !== routes.LOGIN) {
                    window.location.href = Router.generate(routes.LOGIN);
                }
                return Promise.reject(error);
            }

            const refreshToken = UserAuthService.getRefreshToken();
            if (!refreshToken) {
                await UserAuthService.logout();
                if (window.location.pathname !== routes.LOGIN) {
                    window.location.href = Router.generate(routes.LOGIN);
                }
                return Promise.reject(error);
            }

            if (isRefreshing) {
                return new Promise((resolve, reject) => {
                    failedQueue.push({ resolve, reject });
                })
                    .then((token) => {
                        originalRequest.headers['Authorization'] = `Bearer ${token}`;
                        return axios(originalRequest);
                    })
                    .catch((err) => Promise.reject(err));
            }

            originalRequest._retry = true;
            isRefreshing = true;

            try {
                // Call refresh endpoint with direct axios call
                const res = await axios.post(`${API_URL}/auth/refresh`, {
                    refreshToken,
                });
                const data = res?.data;
                if (data && data.token) {
                    UserAuthService.login(data.token, data.refreshToken || refreshToken);
                    originalRequest.headers['Authorization'] = `Bearer ${data.token}`;
                    processQueue(null, data.token);
                    return axios(originalRequest);
                } else {
                    throw new Error('Invalid refresh token response');
                }
            } catch (refreshErr) {
                processQueue(refreshErr, null);
                await UserAuthService.logout();
                if (window.location.pathname !== routes.LOGIN) {
                    window.location.href = Router.generate(routes.LOGIN);
                }
                return Promise.reject(refreshErr);
            } finally {
                isRefreshing = false;
            }
        }

        return Promise.reject(error);
    },
);

class Axios {
    // private authorizationToken = '';
    //
    // // Set authorization token
    // public setAuthorizationToken(token: string): void {
    //     this.authorizationToken = token;
    // }
    //
    // // Drop authorization token
    // public dropAuthorizationToken(): void {
    //     this.authorizationToken = '';
    // }

    public get<T = any>(endPoint: string, params: AnyObject = {}, config: AnyObject = {}): AxiosPromise<T> {
        return axios.get<T>(endPoint, this.addHeaders({ ...config, params }));
    }

    public post<T = any>(endPoint: string, params: AnyObject = {}, config: AnyObject = {}): AxiosPromise<T> {
        return axios.post<T>(endPoint, params, this.addHeaders(config));
    }

    public put<T = any>(endPoint: string, params: AnyObject = {}, config: AnyObject = {}): AxiosPromise<T> {
        return axios.put<T>(endPoint, params, this.addHeaders(config));
    }

    public patch<T = any>(endPoint: string, params: AnyObject = {}, config: AnyObject = {}): AxiosPromise<T> {
        return axios.patch<T>(endPoint, params, this.addHeaders(config));
    }

    public delete<T = any>(endPoint: string, config: AnyObject = {}): AxiosPromise<T> {
        return axios.delete<T>(endPoint, this.addHeaders(config));
    }

    // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
    public delay(time: number, args?: any): AxiosPromise<any> {
        return new Promise((resolve) => {
            setTimeout(resolve.bind(null, args), time);
        });
    }

    public axios<T = any>(config: AxiosRequestConfig): AxiosPromise<T> {
        const resultConfig = this.addHeaders(config);

        return axios(resultConfig);
    }

    addHeaders(userConfig: any): AnyObject {
        let requestHeaders: AnyObject = {
            // Some custom request headers
        };

        // if (this.authorizationToken) {
        //     requestHeaders = { Authorization: `Bearer ${this.authorizationToken}` };
        // }

        if (UserAuthService.isLoggedIn()) {
            requestHeaders = { Authorization: `Bearer ${UserAuthService.getToken()}` };
        }

        const { headers, ...restConfigs } = userConfig;

        // Return extended config
        return {
            headers: {
                ...requestHeaders,
                ...headers,
            },
            ...restConfigs,
        };
    }
}

const EndPointService = new Axios();

export { Axios, EndPointService };
